import { Controller } from '@hotwired/stimulus';
import { GoogleCharts } from 'google-charts';

export default class extends Controller {
  static targets = ['pie', 'column', 'bar', 'radar'];

  static values = {
    radius: Number,
    locale: String,
  };

  connect() {
    GoogleCharts.load(this.drawCharts.bind(this));
    GoogleCharts.load('upcoming', {
      packages: ['vegachart'],
    });
  }

  isCondensed() {
    return this.radiusValue <= 80;
  }

  targetsVisible() {
    return (
      (!this.hasPieTarget || this.pieTarget.offsetParent !== null) &&
      (!this.hasColumnTarget || this.columnTarget.offsetParent !== null) &&
      (!this.hasBarTarget || this.barTarget.offsetParent !== null) &&
      (!this.hasRadarTarget || this.radarTarget.offsetParent !== null)
    );
  }

  drawCharts() {
    // Target elements are placed on a tab which may not be selected yet.
    // Wait until all targets are visible
    const checkExist = setInterval(() => {
      if (this.targetsVisible()) {
        if (this.hasPieTarget) this.drawPieChart();

        if (this.hasColumnTarget) this.drawColumnChart();

        if (this.hasBarTarget) this.drawBarChart();

        if (this.hasRadarTarget) this.drawRadarChart();

        clearInterval(checkExist);
      }
    }, 100);
  }

  drawPieChart() {
    const data = JSON.parse(this.pieTarget.dataset.values);
    data.unshift(['Element', 'Kraft']);
    const colors = JSON.parse(this.pieTarget.dataset.colors);

    const pieData = GoogleCharts.api.visualization.arrayToDataTable(data);

    const pieOptions = {
      colors: colors,
      chartArea: {
        width: '100%',
        height: '100%',
      },
      legend: 'none',
      tooltip: {
        text: 'value',
      },
      backgroundColor: 'transparent',
      pieSliceText: 'label',
    };

    const pieChart = new GoogleCharts.api.visualization.PieChart(
      this.pieTarget,
    );
    pieChart.draw(pieData, pieOptions);
  }

  drawColumnChart() {
    const data = JSON.parse(this.columnTarget.dataset.values);
    const colors = JSON.parse(this.columnTarget.dataset.colors);

    const colData = new GoogleCharts.api.visualization.DataTable();
    colData.addColumn('string', '');
    colData.addColumn('number', '');
    colData.addColumn({ type: 'string', role: 'style' });
    colData.addColumn({ type: 'string', role: 'annotation' });

    colData.addRows(data.length);
    for (let i = 0; i < data.length; i++) {
      colData.setValue(i, 0, data[i][0]);
      colData.setValue(i, 1, data[i][1] / 100);
      colData.setValue(i, 2, colors[i]);
      colData.setValue(i, 3, data[i][1].toString() + ' %');
    }

    const view = new GoogleCharts.api.visualization.DataView(colData);

    const colOptions = {
      backgroundColor: 'transparent',
      legend: { position: 'none' },
      hAxis: {
        slantedText: true,
        slantedTextAngle: 45,
        maxTextLines: 2,
        textStyle: {
          color: 'black',
        },
      },
      vAxis: {
        maxTextLines: 2,
        format: '#%',
        ticks: [
          {
            v: 0.05,
            f: this.localeValue == 'en' ? 'Undersupplied' : 'Unterversorgt',
          },
          {
            v: 0.15,
            f: this.localeValue == 'en' ? 'Oversupplied' : 'Überschuss',
          },
          { v: 0, f: '0%' },
        ],
        gridlines: {
          color: '#cccccc',
        },
        baselineColor: 'black',
      },
      annotations: {
        alwaysOutside: true,
        highContrast: false,
        textStyle: {
          color: 'black',
        },
      },
    };

    const colChart = new GoogleCharts.api.visualization.ColumnChart(
      this.columnTarget,
    );
    colChart.draw(view, colOptions);
  }

  getValueAt(column, dataTable, row) {
    return dataTable.getFormattedValue(row, column) + '%';
  }

  drawBarChart() {
    const data = JSON.parse(this.barTarget.dataset.values);
    const colors = JSON.parse(this.barTarget.dataset.colors);

    const barData = new GoogleCharts.api.visualization.DataTable();
    barData.addColumn('string', '');
    barData.addColumn('number', '');
    barData.addColumn({ type: 'string', role: 'style' });

    barData.addRows(data.length);
    for (let i = 0; i < data.length; i++) {
      barData.setValue(i, 0, data[i][0]);
      barData.setValue(i, 1, data[i][1]);
      barData.setValue(i, 2, colors[i]);
    }
    barData.sort({ column: 1, desc: true });

    const view = new GoogleCharts.api.visualization.DataView(barData);
    view.setColumns([
      0,
      1,
      {
        calc: this.getValueAt.bind(undefined, 1),
        sourceColumn: 1,
        type: 'string',
        role: 'annotation',
      },
      2,
    ]);

    const barOptions = {
      chartArea: {
        top: 0,
        right: 40,
        bottom: 0,
        left: this.element.clientWidth * 0.6,
      },
      backgroundColor: 'transparent',
      legend: { position: 'none' },
      enableInteractivity: false,
      vAxis: {
        gridLines: { count: 10 },
        textStyle: {
          color: this.onPrint ? 'black' : 'lightgray',
        },
      },
      hAxis: {
        gridLines: { count: 5 },
        ticks: [0, 20, 40, 60, 80, 100],
        format: '#',
        viewWindow: {
          min: 0,
          max: 100,
        },
      },
    };

    const barChart = new GoogleCharts.api.visualization.BarChart(
      this.barTarget,
    );
    barChart.draw(view, barOptions);
  }

  drawRadarChart() {
    const data = JSON.parse(this.radarTarget.dataset.values);

    const color = '#C9B95C';

    const dataTable = new GoogleCharts.api.visualization.DataTable();
    dataTable.addColumn({ type: 'string', id: 'key' });
    dataTable.addColumn({ type: 'number', id: 'value' });
    dataTable.addColumn({ type: 'string', id: 'pattern' });
    dataTable.addColumn({ type: 'string', id: 'color' });
    dataTable.addRows(data);

    const radarOptions = {
      vega: {
        width: this.element.clientWidth,
        height: this.element.clientHeight,
        padding: 0,
        autosize: { type: 'none', contains: 'content' },
        signals: [{ name: 'radius', update: this.radiusValue }],
        data: [
          {
            name: 'table',
            source: 'datatable',
          },
        ],
        scales: [
          {
            name: 'angular',
            type: 'point',
            range: { signal: '[-PI, PI]' },
            padding: 0.5,
            domain: { data: 'table', field: 'key' },
          },
          {
            name: 'radial',
            type: 'linear',
            range: { signal: '[0, radius]' },
            zero: true,
            nice: false,
            domain: [0, 1],
          },
        ],
        encode: {
          enter: {
            x: { signal: 'width/2' },
            y: { signal: 'height/2 + 20' },
          },
        },
        marks: [
          {
            type: 'group',
            name: 'patterns',
            zindex: 1,
            from: {
              data: 'table',
            },
            marks: [
              {
                type: 'line',
                name: 'pattern-line',
                from: { data: 'table' },
                encode: {
                  enter: {
                    interpolate: { value: 'linear-closed' },
                    x: {
                      signal:
                        "scale('radial', datum.value) * cos(scale('angular', datum.key))",
                    },
                    y: {
                      signal:
                        "scale('radial', datum.value) * sin(scale('angular', datum.key))",
                    },
                    stroke: { value: color },
                    strokeWidth: { value: 1.5 },
                    fill: { value: color },
                    fillOpacity: { value: 0.4 },
                  },
                },
              },
              this.isCondensed()
                ? null
                : {
                    type: 'text',
                    name: 'value-text',
                    from: { data: 'pattern-line' },
                    encode: {
                      enter: {
                        x: {
                          signal:
                            "datum.x + 14 * cos(scale('angular', datum.datum.key))",
                        },
                        y: {
                          signal:
                            "datum.y + 14 * sin(scale('angular', datum.datum.key))",
                        },
                        text: {
                          signal: "format(datum.datum.value,'.0%')",
                        },
                        opacity: {
                          signal: 'datum.datum.value > 0.01 ? 1 : 0',
                        },
                        align: { value: 'center' },
                        baseline: { value: 'middle' },
                        fontWeight: { value: 'bold' },
                        fill: { value: color },
                      },
                    },
                  },
            ].filter((item) => item),
          },
          {
            type: 'rule',
            name: 'radial-grid',
            from: { data: 'table' },
            zindex: 0,
            encode: {
              enter: {
                x: { value: 0 },
                y: { value: 0 },
                x2: {
                  signal: "radius * cos(scale('angular', datum.key))",
                },
                y2: {
                  signal: "radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'text',
            name: 'key-label',
            from: { data: 'table' },
            zindex: 1,
            encode: {
              enter: {
                x: {
                  signal: "(radius + 25) * cos(scale('angular', datum.key))",
                },
                y: [
                  {
                    test: "sin(scale('angular', datum.key)) > 0",
                    signal: "(radius + 25) * sin(scale('angular', datum.key))",
                  },
                  {
                    test: "sin(scale('angular', datum.key)) < 0",
                    signal: "(radius + 25) * sin(scale('angular', datum.key))",
                  },
                  {
                    signal: "(radius + 20) * sin(scale('angular', datum.key))",
                  },
                ],
                text: { field: 'key' },
                align: {
                  value: 'center',
                },
                baseline: {
                  value: 'middle',
                },
                fill: this.isCondensed()
                  ? { field: 'color' }
                  : { value: 'lightgray' },
                fontSize: { value: 12 },
              },
            },
          },
          {
            type: 'text',
            name: 'key-element',
            from: { data: 'table' },
            zindex: 1,
            encode: {
              enter: {
                x: {
                  signal: "(radius + 20) * cos(scale('angular', datum.key))",
                },
                y: [
                  {
                    test: "sin(scale('angular', datum.key)) > 0",
                    signal: "(radius + 50) * sin(scale('angular', datum.key))",
                  },
                  {
                    test: "sin(scale('angular', datum.key)) < 0",
                    signal: "(radius + 50) * sin(scale('angular', datum.key))",
                  },
                  {
                    signal:
                      "20 + (radius + 50) * sin(scale('angular', datum.key))",
                  },
                ],
                text: { field: 'pattern' },
                align: {
                  value: 'center',
                },
                baseline: {
                  value: 'middle',
                },
                fill: { field: 'color' },
                fontSize: { value: 11 },
              },
            },
          },
          {
            type: 'line',
            name: 'twenty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.2 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.2 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'fourty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.4 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.4 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'sixty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.6 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.6 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'eighty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.8 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.8 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'outer-line',
            from: { data: 'radial-grid' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: { field: 'x2' },
                y: { field: 'y2' },
                stroke: { value: 'lightgray' },
                strokeWidth: { value: 1 },
              },
            },
          },
        ],
      },
    };

    const radarChart = new GoogleCharts.api.visualization.VegaChart(
      this.radarTarget,
    );
    radarChart.draw(dataTable, radarOptions);
  }

  get onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }
}
